import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { getErrorMessage } from '@/shared/data/errors';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { AllowedSignupEmailDto, QueryResult } from '@flowqa/shared';

type AllowedSignupEmailCreateInput = { email: string };

type AllowedSignupEmailDeleteResult = { status: 'deleted' };

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryAllowedSignupEmails = async (
  input: GridQueryInput,
  tokenOverride?: string | null,
): Promise<QueryResult<AllowedSignupEmailDto>> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<QueryResult<AllowedSignupEmailDto>>(
      'superuser/allowed-signups/query',
      { page: input.page, pageSize: input.pageSize, sorts: input.sorts, filters: input.filters },
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const createAllowedSignupEmail = async (
  input: AllowedSignupEmailCreateInput,
  tokenOverride?: string | null,
): Promise<AllowedSignupEmailDto> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<AllowedSignupEmailDto>('superuser/allowed-signups', input, {
      retries: 1,
      token: authToken,
    });
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteAllowedSignupEmail = async (
  input: { id: string },
  tokenOverride?: string | null,
): Promise<AllowedSignupEmailDeleteResult> => {
  const authToken = ensureToken(tokenOverride);
  try {
    return await apiClient.post<AllowedSignupEmailDeleteResult>(
      `superuser/allowed-signups/${input.id}/delete`,
      {},
      { retries: 1, token: authToken },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
